// Table Component
import Table from '../ui/table/Table'

// Mantine
import { Anchor } from '@mantine/core'

// Dayjs
import dayjs from 'dayjs'

// Supabase
import { useApiSWRReference } from '@/hooks/useApiSWR'

// Classnames
import classNames from 'classnames'

// React
import { useState, useEffect } from 'react'

const movementTypes = [
    {
        label: 'Ajustement manuel',
        value: 'manual',
    },
    {
        label: 'Assemblage',
        value: 'production_order',
    },
    {
        label: 'Commande entrante',
        value: 'new_order',
    },
    {
        label: 'Inventaire',
        value: 'inventory',
    },
    {
        label: 'Réception manuelle',
        value: 'reception',
    },
    {
        label: 'Réception bon de commande',
        value: 'purchase_order',
    },
    {
        label: 'Préparation effectuée',
        value: 'fulfillment',
    },
]

export default function ComponentsHistoryTable() {
    // Cells
    const EvolutionCell = (item) => {
        const evolutionString =
            item.evolution >= 0 ? '+' + item.evolution : item.evolution

        const evolutionClass = classNames({
            'text-green-600': item.evolution > 0,
            'text-red-600': item.evolution < 0,
            'text-gray-600': item.evolution === 0,
        })

        return (
            <div className="table-cell p-5 text-gray-600 text-center">
                <span className={evolutionClass}>{evolutionString}</span>
            </div>
        )
    }

    const InvlevelCell = (item) => {
        return (
            <div className="table-cell p-5 text-gray-600 text-center">
                <span>{item.invlevel}</span>
            </div>
        )
    }

    const TypeCell = (item) => {
        const verboseType =
            item.invtype === 'available'
                ? 'Quantité en vente'
                : 'Stock physique'

        return (
            <div className="table-cell p-5 text-sm text-gray-500">
                {verboseType}
            </div>
        )
    }

    const ItemCell = (item) => (
        <div className="table-cell p-5 text-gray-600">
            <pre>{JSON.stringify(item, null, 2)}</pre>
        </div>
    )

    const ReasonCell = (item) => {
        const verboseReason = movementTypes.find(
            (movementType) => movementType.value === item.reason
        )?.label

        return (
            <div className="table-cell p-5 text-gray-500 text-sm">
                <span>{verboseReason}</span>
            </div>
        )
    }

    const ComponentCell = (item) => {
        const component = item.component
        const category = component.component_category

        const name = category.name_fr + ' – ' + component.name

        return (
            <div className="table-cell p-5 text-gray-600">
                <Anchor
                    href={`/components/${item.component_id}`}
                >
                    {name}
                </Anchor>
            </div>
        )
    }

    const DateCell = (item) => (
        <div className="table-cell p-5 text-gray-500 text-sm">
            {dayjs(item.created_at).format('ll à HH:mm')}
        </div>
    )

    const ProfileCell = (item) => (
        <div className="table-cell p-5 text-gray-500 text-sm">
            <span>{item.profile?.name}</span>
        </div>
    )

    const LocationCell = (item) => (
        <div className="table-cell p-5 text-gray-500 text-sm">
            <span>{item.location?.name}</span>
        </div>
    )

    // Columns
    const availableColumns = [
        {
            name: 'Date',
            key: 'created_at',
            display: 'mandatory',
            sortable: true,
            component: DateCell,
            width: 220,
        },
        {
            name: 'Évolution',
            key: 'evolution',
            display: 'optional_default',
            width: 100,
            component: EvolutionCell,
        },
        {
            name: 'Niveau de stock',
            key: 'invlevel',
            display: 'optional',
            width: 100,
            component: InvlevelCell,
        },
        {
            name: 'Type',
            key: 'invtype',
            display: 'optional_default',
            width: 200,
            component: TypeCell,
        },
        {
            name: 'Composant',
            key: 'id',
            display: 'optional_default',
            component: ComponentCell,
        },
        {
            name: 'Raison',
            key: 'reason',
            display: 'optional_default',
            width: 300,
            component: ReasonCell,
        },
        {
            name: 'Lieu',
            key: 'location',
            display: 'optional_default',
            width: 300,
            component: LocationCell,
        },
        {
            name: 'Utilisateur',
            key: 'profile',
            display: 'optional',
            width: 200,
            component: ProfileCell,
        },
    ]

    // Filters
    const { data: categories } = useApiSWRReference('/component-categories')

    const categoryOptions =
        categories?.map((c) => ({
            value: c.slug,
            label: c.name_fr,
        })) ?? []

    const availableFilters = [
        {
            name: 'Catégorie de composant',
            key: 'components.category',
            type: 'multiselect',
            placeholder: 'Cadran, boitier, tête de montre, bracelet...',
            options: categoryOptions,
        },
        {
            name: 'Type de mouvement de stock',
            key: 'invtype',
            type: 'select',
            options: [
                { value: 'available', label: 'Quantité en vente' },
                { value: 'physical', label: 'Stock physique' },
            ],
        },
        {
            name: 'Type de mouvement',
            key: 'reason',
            type: 'select',
            options: movementTypes,
        },
    ]

    return (
        <Table
            engineApiEndpoint="/component-invlevels-history"
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            searchable={true}
            defaultSortColumn="created_at"
            defaultSortDirection="desc"
        />
    )
}
